import { useTranslation } from "react-i18next";
import type { CSSProperties } from "react";
import "./tablePagination.scss";

type TablePaginationProps = {
    page: number;
    setPage: (value: number | ((prev: number) => number)) => void;
    pageSize: number;
    setPageSize: (size: number) => void;
    totalPages: number;
    totalCount: number;
    pageSizes: readonly number[];
    /** Roster bar above list rows (new-style lists). */
    placement?: "default" | "roster-top";
    accentColor?: string;
    loading?: boolean;
};

export default function TablePagination({
    page,
    setPage,
    pageSize,
    setPageSize,
    totalPages,
    totalCount,
    pageSizes,
    placement = "default",
    accentColor,
    loading,
}: TablePaginationProps) {
    const { t } = useTranslation();

    if (loading && placement === "roster-top") {
        return (
            <div className="table-pagination--roster-top-skeleton">
                <div className="table-pagination--roster-top-skeleton__left">
                    <span className="table-pagination--roster-top-skeleton__pill table-pagination--roster-top-skeleton__pill--label" />
                    {pageSizes.map((s) => (
                        <span key={s} className="table-pagination--roster-top-skeleton__pill table-pagination--roster-top-skeleton__pill--sm" />
                    ))}
                </div>
                <div className="table-pagination--roster-top-skeleton__nav">
                    {[1, 2, 3, 4].map((n) => (
                        <span key={n} className="table-pagination--roster-top-skeleton__btn" />
                    ))}
                    <span className="table-pagination--roster-top-skeleton__info" />
                    {[5, 6, 7, 8].map((n) => (
                        <span key={n} className="table-pagination--roster-top-skeleton__btn" />
                    ))}
                </div>
            </div>
        );
    }

    const className =
        placement === "roster-top"
            ? "table-pagination table-pagination--roster-top"
            : "table-pagination";

    const style =
        placement === "roster-top" && accentColor
            ? ({ "--pagination-accent": accentColor } as CSSProperties)
            : undefined;

    return (
        <div className={className} style={style} role="navigation" aria-label={t("invoices.perPage")}>
            <div className="table-pagination__size">
                <span>{t("invoices.perPage")}</span>
                {pageSizes.map((s) => (
                    <button
                        key={s}
                        type="button"
                        className={`table-pagination__size-btn${pageSize === s ? " table-pagination__size-btn--active" : ""}`}
                        onClick={() => setPageSize(s)}
                    >
                        {s}
                    </button>
                ))}
            </div>
            <div className="table-pagination__nav">
                <button
                    type="button"
                    className="table-pagination__btn"
                    disabled={page === 0}
                    onClick={() => setPage(0)}
                >
                    «
                </button>
                <button
                    type="button"
                    className="table-pagination__btn"
                    disabled={page === 0}
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                    ‹
                </button>
                <span className="table-pagination__info">
                    {page + 1} / {totalPages}
                    <span className="table-pagination__total">
                        ({totalCount} {t("invoices.total")})
                    </span>
                </span>
                <button
                    type="button"
                    className="table-pagination__btn"
                    disabled={page >= totalPages - 1}
                    onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                >
                    ›
                </button>
                <button
                    type="button"
                    className="table-pagination__btn"
                    disabled={page >= totalPages - 1}
                    onClick={() => setPage(totalPages - 1)}
                >
                    »
                </button>
            </div>
        </div>
    );
}
